import React, { useEffect, useRef, useState } from 'react'
import { Col, Form, Row, notification } from 'antd'
import { Formik } from 'formik'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ApplicationState } from 'src/store'
import { Card } from 'src/components/Ant'
import {
  CostCenter,
  Request,
  RequestStandardSharing,
  RequestUserData
} from 'src/types'
import { DatePickerRange, Input, TextArea } from 'src/components/Formik'
import {
  getInitialValues,
  getValidationSchema
} from 'src/core/forms/request/setup'
import { getResources } from 'src/core/store/ducks/resources/actions'
import { getUsers } from 'src/core/store/ducks/users/actions'
import { resetAir } from 'src/core/store/ducks/air/actions'
import { resetHotels } from 'src/core/store/ducks/hotels/actions'
import { resetVehicles } from 'src/core/store/ducks/vehicles/actions'
import { useStepsContext } from 'src/views/Requests/views/Form'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import useStandardResource from 'src/core/hooks/use-standard-resource'
import { WrapRowJustification } from './styles'
import AdditionalInformation from './components/AdditionalInformation'
import SetupUserList from './components/setup-user-list'
import { requestStandardSharingsReduce } from '../Assignment/components/Budget/budget-summary'
import { WrapCard, WrapRow } from '../styles'
import StepsNavigation from '../../StepsNavigation'

interface Params {
  reason_id: string
}

interface GroupSharings {
  cc: CostCenter
  score: number
}

const initialValues = getInitialValues()

function calculateSharings(sharings: CostCenter[]): GroupSharings[] {
  if (!sharings?.length) {
    return []
  }

  const newSharings = sharings
    .filter(({ active, cost_center_id }) => cost_center_id && active)
    .reduce((acc, cur: CostCenter) => {
      const findIndex = acc?.findIndex(
        i => i.cc.cost_center_id === cur.cost_center_id
      )
      if (findIndex <= -1) {
        acc.push({ cc: cur, score: 1 })
      } else {
        acc[findIndex].score++
      }
      return acc
    }, [])
    .sort((a, b) => b.score - a.score) as GroupSharings[]

  const totalCCs = newSharings.reduce(
    (acc: number, { score }) => acc + score,
    0
  )
  const part = Math.floor(100 / totalCCs)
  const rest = 100 - part * totalCCs

  return newSharings.reduce((acc, { cc, score }, index) => {
    acc.push(
      !index ? { cc, score: score * part + rest } : { cc, score: score * part }
    )
    return acc
  }, [])
}

function Setup({
  match: {
    params: { reason_id }
  }
}: Readonly<RouteComponentProps<Params>>) {
  const {
    me,
    requests,
    resources,
    users: { list, error, loading }
  } = useSelector((state: ApplicationState) => ({
    me: state.me,
    requests: state.requests,
    resources: state.resources,
    users: state.users
  }))

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const policyRule = useReasonPolicyRule(reason_id)

  const [needJustification, setNeedJustification] = useState<boolean>(false)
  const [requestedOnHelper, setRequestedOnHelper] = useState<number>(
    initialValues?.requested_on
  )

  const refFormik = useRef<Formik<Request>>(null)

  const stepsContext = useStepsContext()
  const contractStandard = useStandardResource('contracts')

  useEffect(() => {
    if (!loading && error && me.user.requester) {
      notification.error({
        message: t('common.response.unexpected-error'),
        placement: 'bottomRight'
      })
    }
  }, [error, loading, me, t])

  useEffect(() => {
    if (me.user.requester) {
      dispatch(getUsers())
    }
    dispatch(
      getResources('contracts', {
        organization_id:
          stepsContext.request.organization_id ||
          me.organization.organization_id
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setTimeout(async () => {
      if (!refFormik.current) {
        return
      }

      let request_user_data =
        refFormik.current.getFormikBag().values.request_user_data

      if (!request_user_data.length) {
        const reason = resources.reasons.list.find(
          reasonItem => +reasonItem.reason_id === +reason_id
        )

        request_user_data = [
          {
            cost_center: me.user.cost_center,
            included: true,
            reason_id: reason?.reason_id,
            reason: reason,
            traveler: {
              user_id: me.user.user_id,
              name: `${me.user.first_name} ${me.user.last_name}`,
              gender: me.user.gender
            },
            user_id: me.user.user_id
          }
        ]

        refFormik.current.setFieldValue('request_user_data', request_user_data)
      }
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.user, reason_id])

  /**
   * Load request data on form
   */
  useEffect(() => {
    if (!stepsContext.request) {
      return
    }
    /**
     * @todo remove ts-ignore
     */
    const keys = Object.keys(initialValues)

    let newInitialValues: typeof initialValues = { ...initialValues }
    // @ts-ignore
    keys.map(key => (newInitialValues[key] = stepsContext.request[key]))

    if (
      policyRule?.policy_request_general_rule?.policy_request_general_rule_id &&
      !stepsContext.request.title.trim()
    ) {
      // for new request
      const { active, minimum_days_limit, value } =
        policyRule?.policy_request_general_rule

      if (active && minimum_days_limit) {
        const newRequestOn = moment
          .unix(initialValues.requested_on)
          .add(value, 'days')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .unix()
        const date_end = moment
          .unix(initialValues.requested_on)
          .add(value + 1, 'days')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .unix()
        newInitialValues = {
          ...newInitialValues,
          requested_on: newRequestOn,
          date_end
        }
      }
    }
    refFormik.current.resetForm(newInitialValues)
    setRequestedOnHelper(newInitialValues.requested_on)
  }, [stepsContext.request, policyRule])

  useEffect(() => {
    if (
      policyRule?.policy_request_general_rule?.policy_request_general_rule_id
    ) {
      if (requestedOnHelper) {
        const { action, active, minimum_days_limit, value } =
          policyRule.policy_request_general_rule

        if (active && minimum_days_limit && !value) {
          setNeedJustification(true)
          return
        }
        const conditionsJustification =
          active &&
          minimum_days_limit &&
          action === 'ALERT' &&
          moment()
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .add(value, 'days')
            .unix() >
            moment
              .unix(requestedOnHelper)
              .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
              .unix()
        setNeedJustification(conditionsJustification)
      }
    }
  }, [policyRule, requestedOnHelper])

  function handleSubmit(values: typeof initialValues) {
    let newSharings: RequestStandardSharing[] = []
    if (
      !requests.fullRequest?.request_standard_assignments
        .request_standard_sharings.length
    ) {
      const hasContractAdicionalInfo = values.request_custom_data[0]?.contract
      const contract = values.request_standard_assignments
        ?.request_standard_sharings[0]?.contract?.contract_id
        ? values.request_standard_assignments.request_standard_sharings[0]
            .contract
        : hasContractAdicionalInfo?.contract_id
        ? hasContractAdicionalInfo
        : contractStandard

      if (contract && contract.contract_id) {
        const groupUserSharings = calculateSharings(
          values.request_user_data
            .filter(item => !!item.cost_center)
            .map(item => item.cost_center)
        )

        if (groupUserSharings.length) {
          newSharings = groupUserSharings.map(item => {
            return {
              active: true,
              contract_id: contract.contract_id.toString(),
              contract: {
                active: true,
                code: contract.code,
                contract_id: contract.contract_id,
                description: contract.description,
                name: contract.name,
                organization_id: contract.organization_id,
                shared_with: null,
                standard: true
              },
              cost_center_id: item.cc.cost_center_id.toString(),
              cost_center: {
                active: true,
                code: item.cc.code,
                cost_center_id: item.cc.cost_center_id,
                description: item.cc.description,
                name: item.cc.name,
                organization_id: item.cc.organization_id,
                shared_with: null,
                standard: false
              },
              percentage: item.score,
              request_id: null,
              request_standard_sharing_id: null,
              request_standard_sharing_number: null,
              request_ticket_sharing_type: 'UNIFIED' as const,
              tmp_id: Math.random()
            } as RequestStandardSharing
          })
        }
      }
    }

    const setRequest = {
      ...stepsContext.request,
      ...values,
      ...(!newSharings.length
        ? {}
        : {
            request_standard_assignments: {
              request_standard_billings:
                values.request_standard_assignments.request_standard_billings,
              request_standard_sharings: newSharings
            }
          })
    }

    const budgets =
      setRequest.request_standard_assignments.request_standard_sharings.reduce(
        requestStandardSharingsReduce,
        []
      )

    stepsContext.setBudgetAllocations({ budgets })
    stepsContext.setRequest(setRequest)
    stepsContext.next()
  }

  function dispatchUsers(value: string = '') {
    dispatch(getUsers(value))
  }

  /**
   * This function must check if it is possible to change the travelers.
   * Ex: After selecting an airline ticket, you cannot add a new traveler because he will be without his airline.
   * When selecting an airline, all participants will have a ticket.
   */
  function verifyCanBeChange(): boolean {
    const {
      full_request_air,
      full_request_hotel,
      full_request_vehicle,
      request_tickets,
      request_standard_assignments: {
        request_standard_billings,
        request_standard_sharings
      }
    } = stepsContext.request

    return !(
      full_request_air.length ||
      full_request_hotel.length ||
      full_request_vehicle.length ||
      request_standard_billings.length ||
      request_standard_sharings.length ||
      request_tickets.length
    )
  }

  function resetRequest(request_user_data: RequestUserData[]): void {
    stepsContext.setRequest({
      ...stepsContext.request,
      full_request_air: [],
      full_request_hotel: [],
      full_request_vehicle: [],
      request_standard_assignments: {
        request_standard_billings: [],
        request_standard_sharings: []
      },
      request_tickets: [],
      request_user_data
    })

    stepsContext.setBudgetAllocations({
      budgets: [],
      tickets: []
    })

    dispatch(resetAir())
    dispatch(resetHotels())
    dispatch(resetVehicles())
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      ref={refFormik}
      render={formikProps => (
        <Form onSubmit={formikProps.handleSubmit}>
          <StepsNavigation onNext={formikProps.submitForm} />
          <WrapRow>
            <Col span={15}>
              <WrapCard
                bodyStyle={{ minHeight: 160, marginBottom: '16px' }}
                data-test-id="setup-card"
              >
                <Row gutter={24}>
                  <Col span={16}>
                    <Input
                      autoFocus
                      data-test-id="setup-title"
                      formikProps={formikProps}
                      label="web.setup.form.field-title.title"
                      name="title"
                      placeholder="web.setup.form.field-title.placeholder"
                    />
                  </Col>
                  <Col span={8}>
                    <DatePickerRange
                      onChange={value => {
                        if (value[0]) {
                          setRequestedOnHelper(value[0].unix())
                        }
                      }}
                      formikProps={formikProps}
                      label="web.setup.form.start-date.title"
                      name={['requested_on', 'date_end']}
                      placeholder={[
                        'web.setup.form.start-date.placeholder',
                        'web.reports.parameter.period.placeholder-end'
                      ]}
                      showTime={false}
                    />
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={24}>
                    <TextArea
                      allowClear
                      data-test-id="setup-obs"
                      formikProps={formikProps}
                      label="web.assignment.billing.form.observation.title"
                      name="obs"
                      placeholder="web.assignment.billing.form.observation.placeholder"
                      required={false}
                      rows={3}
                    />
                  </Col>
                </Row>
                <WrapRowJustification visible={Number(needJustification)}>
                  <Col>
                    <Input
                      autoFocus
                      formikProps={formikProps}
                      label="web.setup.form.justification.title"
                      name="justification"
                      placeholder="web.setup.form.justification.placeholder"
                    />
                  </Col>
                </WrapRowJustification>
              </WrapCard>
              <WrapRow ml={0} mr={0}>
                <Col>
                  {!!policyRule &&
                    (policyRule.enable_def_custom_contract ||
                      policyRule.enable_def_custom_target) && (
                      <AdditionalInformation
                        enableContract={policyRule.enable_def_custom_contract}
                        enableTarget={policyRule.enable_def_custom_target}
                        formikProps={formikProps}
                        organization_id={stepsContext.request.organization_id}
                      />
                    )}
                </Col>
              </WrapRow>
            </Col>
            <Col offset={1} span={8}>
              <Card
                data-test-id="travelers-card"
                bodyStyle={{ maxHeight: 460, overflow: 'auto' }}
                title="web.setup.participants.title"
              >
                <SetupUserList
                  formikProps={formikProps}
                  getUsers={dispatchUsers}
                  loading={loading}
                  request={stepsContext.request}
                  resetRequest={resetRequest}
                  users={list}
                  verifyCanBeChange={verifyCanBeChange}
                />
              </Card>
            </Col>
          </WrapRow>
        </Form>
      )}
      validateOnBlur={false}
      validationSchema={getValidationSchema({ policyRule })}
    />
  )
}

export default withRouter(Setup)
